// API client do módulo Chat (PRD 2)
// Endpoints: /api/v1/chat/*

import api from "./api";
import type {
  ChatRoom,
  ChatRoomCreate,
  ChatRoomMember,
  ChatRoomReadState,
  ChatMessage,
  ChatMessageCreate,
  ChatMessageEdit,
  ResultadoDaPesquisa,
} from "@/types/chat";

const BASE = "/chat";

export interface PaginatedMessages {
  items: ChatMessage[];
  next_cursor?: string;
  has_more: boolean;
}

const chatService = {
  // Rooms
  listRooms: async (params?: { archived?: boolean; room_type?: string }) => {
    const r = await api.get<ChatRoom[]>(`${BASE}/rooms`, { params });
    return r.data;
  },

  getRoom: async (roomId: string) => {
    const r = await api.get<ChatRoom>(`${BASE}/rooms/${roomId}`);
    return r.data;
  },

  createRoom: async (data: ChatRoomCreate) => {
    const r = await api.post<ChatRoom>(`${BASE}/rooms`, data);
    return r.data;
  },

  // Passo 18 — "Minhas notas": abre (200) ou cria (201) a sala pessoal de quem pede. Idempotente. O caminho
  // NUNCA leva `/api/v1` (o VITE_API_URL já o traz).
  abrirMinhasNotas: async () => {
    const r = await api.post<ChatRoom>(`${BASE}/notas`);
    return r.data;
  },

  updateRoom: async (roomId: string, data: Partial<ChatRoomCreate>) => {
    // Backend usa PATCH (chat.py:153) — PUT retorna 405.
    const r = await api.patch<ChatRoom>(`${BASE}/rooms/${roomId}`, data);
    return r.data;
  },

  archiveRoom: async (roomId: string) => {
    await api.delete(`${BASE}/rooms/${roomId}`);
  },

  // Members
  listMembers: async (roomId: string) => {
    const r = await api.get<ChatRoomMember[]>(`${BASE}/rooms/${roomId}/members`);
    return r.data;
  },

  addMember: async (roomId: string, userId: string, isAdmin = false) => {
    const r = await api.post<ChatRoomMember>(`${BASE}/rooms/${roomId}/members`, {
      user_id: userId,
      is_admin: isAdmin,
    });
    return r.data;
  },

  removeMember: async (roomId: string, userId: string) => {
    await api.delete(`${BASE}/rooms/${roomId}/members/${userId}`);
  },

  // Messages
  listMessages: async (
    roomId: string,
    params?: { before?: string; limit?: number; parent_id?: string }
  ) => {
    // Backend retorna DESC (mais nova → mais antiga) — padrão pra paginação
    // cursor-up "carregar mensagens anteriores". UI renderiza top→bottom
    // como WhatsApp/Slack/Telegram, então invertemos pra ASC aqui:
    // mais antigas no topo, mais nova no rodapé. `useSendMessage` faz
    // `[...old, newMsg]` (append) que então fica visualmente em baixo.
    // Fix do bug "mensagem nova vai pra cima" reportado em 2026-05-18.
    const r = await api.get<ChatMessage[]>(`${BASE}/rooms/${roomId}/messages`, { params });
    return [...r.data].reverse();
  },

  sendMessage: async (roomId: string, data: ChatMessageCreate) => {
    const r = await api.post<ChatMessage>(`${BASE}/rooms/${roomId}/messages`, data);
    return r.data;
  },

  // Story E6.2.2/E6.2.3: backend é PATCH /chat/messages/{id} (sem roomId no path)
  // — janela 24h validada server-side, 403 "Edit window expired" se passou,
  // 409 "Cannot edit a deleted message" se já tombstone.
  editMessage: async (messageId: string, content: any) => {
    const r = await api.patch<ChatMessage>(`${BASE}/messages/${messageId}`, {
      content,
    });
    return r.data;
  },

  // Story E6.2.2: DELETE /chat/messages/{id} (sem roomId). Soft-delete substitui
  // content pelo tombstone literal `{text:"[mensagem apagada]", tombstone:true, ...}`.
  // 204 sem body.
  deleteMessage: async (messageId: string) => {
    await api.delete(`${BASE}/messages/${messageId}`);
  },

  // Story E6.2.2: lista versões anteriores de uma mensagem (history viewer
  // da E6.2.3). Ordem edited_at DESC (mais recente primeiro). 404 se non-member.
  listMessageEdits: async (messageId: string) => {
    const r = await api.get<ChatMessageEdit[]>(`${BASE}/messages/${messageId}/edits`);
    return r.data;
  },

  getReplies: async (roomId: string, messageId: string) => {
    const r = await api.get<ChatMessage[]>(`${BASE}/rooms/${roomId}/messages/${messageId}/replies`);
    return r.data;
  },

  listReadBy: async (roomId: string, messageId: string) => {
    const r = await api.get<Array<{ user_id: string; read_at: string }>>(
      `${BASE}/rooms/${roomId}/messages/${messageId}/read-by`
    );
    return r.data;
  },

  markRead: async (roomId: string, lastReadAt?: string) => {
    await api.post(`${BASE}/rooms/${roomId}/read`, {
      last_read_at: lastReadAt,
    });
  },

  // Story E6.3.3 — snapshot dos cursores delivered/read + opt-out flag de
  // todos os members da sala. Cliente usa pra computar status visual
  // (1/2 gray/2 blue) de cada msg comparando com msg.created_at.
  getReadState: async (roomId: string) => {
    const r = await api.get<ChatRoomReadState>(`${BASE}/rooms/${roomId}/read-state`);
    return r.data;
  },

  // Story E6.3.3 — toggle do opt-out de read receipts NESTA sala. Self-action
  // (qualquer member); idempotente. Quando enabled=false, mark_read futuros
  // não disparam broadcast → sender vê só 1 check pra este member.
  toggleReadReceipts: async (roomId: string, enabled: boolean) => {
    const r = await api.patch<{
      room_id: string;
      user_id: string;
      read_receipts_enabled: boolean;
    }>(`${BASE}/rooms/${roomId}/read-receipts`, { enabled });
    return r.data;
  },

  // D2 (passo 14) — silencia/reativa os avisos de UMA conversa. Self-action (só o próprio
  // membro; 404 se não é membro); idempotente. A conversa silenciada continua contando as não
  // lidas, só não avisa nem toca. O caminho NUNCA leva `/api/v1` (o VITE_API_URL já o traz).
  setNotificationsMuted: async (roomId: string, muted: boolean) => {
    const r = await api.patch<{
      room_id: string;
      user_id: string;
      notifications_muted: boolean;
    }>(`${BASE}/rooms/${roomId}/notifications-muted`, { muted });
    return r.data;
  },

  // Story E6.4.3 — toggle de reaction emoji. Idempotente em pares
  // (mesmo emoji do mesmo user 2x volta ao estado original). Backend
  // valida shape e member-of-room. UI normalmente reduz cache via WS
  // `reaction:update`, mas o response.reactions cobre clients sem WS open.
  //
  // Task 11 — `reactionKey` recebe a mesma chave que o agregado usa:
  // unicode direto (`"😺"`) ou `"custom:{uuid}"` pra emoji da empresa
  // (ver ReactionBar.tsx/EmojiQuickPicker.tsx). É AQUI que o prefixo é
  // traduzido de volta pro corpo que o backend espera
  // (`ReactionToggleRequest`: `emoji` OU `custom_emoji_id`, nunca os
  // dois) — sem essa tradução um clique customizado mandaria
  // `{emoji: "custom:uuid"}` e o backend devolveria 422 (string > 32
  // chars / canal errado).
  toggleReaction: async (messageId: string, reactionKey: string) => {
    const body = reactionKey.startsWith("custom:")
      ? { custom_emoji_id: reactionKey.slice("custom:".length) }
      : { emoji: reactionKey };
    const r = await api.post<{
      message_id: string;
      action: "added" | "removed";
      reactions: Record<string, string[]>;
    }>(`${BASE}/messages/${messageId}/reactions`, body);
    return r.data;
  },

  // Story E6.4.2 — filtro tenant-wide por hashtag. Strip `#` decorativo
  // antes de enviar (backend aceita ambos mas evita ambiguidade no log).
  // Lowercase aplicada client-side pra match consistente com o que o
  // parser server-side persistiu.
  listMessagesByHashtag: async (hashtag: string, params?: { before?: string; limit?: number }) => {
    const tag = hashtag.replace(/^#/, "").toLowerCase();
    const r = await api.get<ChatMessage[]>(`${BASE}/messages`, {
      params: { hashtag: tag, ...params },
    });
    // Backend retorna DESC; UI mostra DESC mesmo (modal "recent first").
    return r.data;
  },

  // Pesquisa na conversa (16/10/2026): mensagens da sala com o termo no texto, na legenda ou no nome do arquivo.
  searchMessages: async (
    roomId: string,
    params: { q: string; before?: string; limit?: number }
  ): Promise<ResultadoDaPesquisa> => {
    const r = await api.get<ResultadoDaPesquisa>(`${BASE}/rooms/${roomId}/messages/search`, {
      params,
    });
    return r.data;
  },

  // Pesquisa em todas as conversas (26/10/2026): o mesmo resultado, nas salas de que a pessoa participa.
  searchAllMessages: async (params: {
    q: string;
    before?: string;
    limit?: number;
  }): Promise<ResultadoDaPesquisa> => {
    const r = await api.get<ResultadoDaPesquisa>(`${BASE}/messages/search`, { params });
    return r.data;
  },

  // Story 2.7.2: enfileira job ARQ export_chat_history. Backend retorna 202
  // imediato + job_id; usuário recebe email com presigned URL TTL 24h quando
  // a job termina. Endpoint REAL é PLURAL `/exports` (singular antigo nunca
  // existiu — heritage de versão que nunca foi mergeada).
  exportHistory: async (roomId: string) => {
    const r = await api.post<{
      status: "queued";
      job_id: string | null;
      room_id: string;
    }>(`${BASE}/rooms/${roomId}/exports`);
    return r.data;
  },

  // ---------------------------------------------------------------------------
  // Entity comments (ADR-204 / E10.1.5) — alias que resolve a sala virtual
  // ENTITY_COMMENTS por (entity_type, entity_id), criando sob demanda.
  //
  // Backend já normaliza ordering DESC (mensagem nova primeiro) — invertemos
  // pra ASC aqui (mais antigas no topo, como WhatsApp/Slack/Telegram).
  // ---------------------------------------------------------------------------
  listEntityComments: async (
    entityType: string,
    entityId: string,
    params?: { before?: string; limit?: number; include_deleted?: boolean }
  ) => {
    const r = await api.get<ChatMessage[]>(
      `${BASE}/entity-comments/${entityType}/${entityId}/messages`,
      { params }
    );
    return [...r.data].reverse();
  },

  postEntityComment: async (entityType: string, entityId: string, data: ChatMessageCreate) => {
    const r = await api.post<ChatMessage>(
      `${BASE}/entity-comments/${entityType}/${entityId}/messages`,
      data
    );
    return r.data;
  },
};

export default chatService;
export { chatService };
